import { __ } from "@wordpress/i18n";
import {
	useBlockProps,
	InspectorControls,
	PanelColorSettings,
} from "@wordpress/block-editor";
import {
	PanelBody,
	ToggleControl,
	SelectControl,
	RangeControl,
	TextControl,
} from "@wordpress/components";
import "./editor.scss";

// Import social media icons.
import twitter from "../../assets/icons/twitter.svg";
import facebook from "../../assets/icons/facebook.svg";
import whatsapp from "../../assets/icons/whatsapp.svg";
import linkedin from "../../assets/icons/linkedin.svg";
import reddit from "../../assets/icons/reddit.svg";
import telegram from "../../assets/icons/telegram.svg";
import email from "../../assets/icons/email.svg";

export const ALL_PLATFORMS = {
	twitter: { label: "Twitter", icon: twitter },
	facebook: { label: "Facebook", icon: facebook },
	whatsapp: { label: "WhatsApp", icon: whatsapp },
	linkedin: { label: "LinkedIn", icon: linkedin },
	reddit: { label: "Reddit", icon: reddit },
	telegram: { label: "Telegram", icon: telegram },
	email: { label: "Email", icon: email },
};

export default function Edit({ attributes, setAttributes }) {
	const {
		enabledPlatforms,
		layoutDirection,
		gap,
		iconSize,
		iconRadius,
		iconColor,
		iconBgColor,
		iconPadding,
	} = attributes;

	const onTogglePlatform = (platform, isChecked) => {
		setAttributes({
			enabledPlatforms: {
				...enabledPlatforms,
				[platform]: isChecked,
			},
		});
	};

	const enabledCount = Object.values(enabledPlatforms).filter(Boolean).length;

	return (
		<>
			<InspectorControls>
				{/* Social Platforms Panel */}
				<PanelBody title={__("Social Platforms", "swift-fse-blocks")}>
					{Object.keys(ALL_PLATFORMS).map((platform) => (
						<ToggleControl
							key={platform}
							label={ALL_PLATFORMS[platform].label}
							checked={!!enabledPlatforms[platform]}
							onChange={(isChecked) => onTogglePlatform(platform, isChecked)}
						/>
					))}
				</PanelBody>

				{/* Layout & Style Panel */}
				<PanelBody
					title={__("Layout & Style", "swift-fse-blocks")}
					initialOpen={false}
				>
					<SelectControl
						label={__("Layout Direction", "swift-fse-blocks")}
						value={layoutDirection}
						options={[
							{ label: __("Row", "swift-fse-blocks"), value: "row" },
							{ label: __("Column", "swift-fse-blocks"), value: "column" },
						]}
						onChange={(value) => setAttributes({ layoutDirection: value })}
					/>

					<RangeControl
						label={__("Gap Between Icons (px)", "swift-fse-blocks")}
						value={gap}
						onChange={(value) => setAttributes({ gap: value })}
						min={0}
						max={50}
					/>

					<RangeControl
						label={__("Icon Size (px)", "swift-fse-blocks")}
						value={iconSize}
						onChange={(value) => setAttributes({ iconSize: value })}
						min={12}
						max={80}
					/>
					<PanelColorSettings
						title={__("Icon Color", "swift-fse-blocks")}
						colorSettings={[
							{
								value: iconColor,
								onChange: (color) => setAttributes({ iconColor: color }),
								label: __("Icon Color", "swift-fse-blocks"),
							},
						]}
					/>
					<PanelColorSettings
						title={__("Icon Background Color", "swift-fse-blocks")}
						colorSettings={[
							{
								value: iconBgColor,
								onChange: (bgColor) => setAttributes({ iconBgColor: bgColor }),
								label: __("Icon Background Color", "swift-fse-blocks"),
							},
						]}
					/>
					<TextControl
						label={__("Icon Padding (px)", "swift-fse-blocks")}
						value={iconPadding}
						onChange={(padding) => setAttributes({ iconPadding: padding })}
						type="number"
					/>
					<RangeControl
						label={__("Icon Border Radius (px)", "swift-fse-blocks")}
						value={iconRadius}
						onChange={(bRadius) => setAttributes({ iconRadius: bRadius })}
						min={1}
						max={100}
					/>
				</PanelBody>
			</InspectorControls>

			{/* Block Editor Preview */}
			<div
				{...useBlockProps({
					style: {
						display: "flex",
						flexDirection: layoutDirection,
						gap: `${gap}px`,
						color: iconColor,
					},
				})}
			>
				{Object.keys(enabledPlatforms)
					.filter((platform) => enabledPlatforms[platform])
					.map((platform) => (
						<span
							key={platform}
							className={`share-icon-preview ${platform}`}
							title={ALL_PLATFORMS[platform].label}
							style={{
								fontSize: `${iconSize}px`,
								lineHeight: "1",
								color: iconColor,
								backgroundColor: iconBgColor,
								borderRadius: `${iconRadius}px`,
								padding: `${iconPadding}px`,
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<img
								src={ALL_PLATFORMS[platform].icon}
								alt={ALL_PLATFORMS[platform].label}
								style={{
									width: iconSize,
									height: iconSize,
									filter: `invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(0%)`,
								}}
							/>
						</span>
					))}
				{enabledCount === 0 && (
					<span style={{ color: "#999", fontSize: "13px" }}>
						{__("No platforms selected", "swift-fse-blocks")}
					</span>
				)}
			</div>
		</>
	);
}
